/**
 * USVI Pirate's Guild — Event Templates (code-first catalog, v1).
 *
 * Each template is a starting point for the business-side event creation
 * wizard. Selecting one seeds the form with sane defaults; everything
 * remains editable.
 *
 * Shape:
 *   {
 *     key:                string,          // stable identifier persisted as event.templateKey
 *     label:              string,          // shown in template picker
 *     icon:               IconDefinition,  // FontAwesome
 *     category:           string,          // grouping: drinks | food | music | wellness | outdoors | community | celebration | special
 *     shortDescription:   string,          // one-line pitch shown on template card
 *     helpfulNotes:       string,          // tip text shown in wizard step 1
 *     defaults: {
 *       type:                       string,           // events.type
 *       intention:                  string,           // free-form, mostly for analytics
 *       title:                      string,
 *       description:                string,
 *       tags:                       string[],
 *       recurrence:                 RecurrenceRule | null,
 *       timeWindow:                 { start: "HH:MM", end: "HH:MM" } | null,
 *       durationMinutes:            number | null,
 *       capacity:                   number | null,
 *       doubloonReward:             number,
 *       ticketPriceCents:           number,
 *       graceWindowMinutes:         { before: number, after: number },
 *       suggestedRedeemable:        { name, description, tokenCost, maxRedemptionsPerUser } | null,
 *     },
 *   }
 *
 * Note: import path-safe — uses only FontAwesome free-solid icons.
 */

import {
  faGlassMartiniAlt,
  faMusic,
  faPuzzlePiece,
  faMicrophone,
  faMicrophoneAlt,
  faShip,
  faPepperHot,
  faWineGlass,
  faVenus,
  faFootballBall,
  faFire,
  faCompactDisc,
  faSpa,
  faKitchenSet,
  faMugSaucer,
  faAnchor,
  faMapMarkedAlt,
  faHandshake,
  faStar,
  faTicket,
  faBirthdayCake,
  faSnowflake,
  faCalendarPlus,
} from "@fortawesome/free-solid-svg-icons";

import { EVENT_TYPE_KEYS } from "./events";

export const TEMPLATE_CATEGORIES = Object.freeze([
  { key: "drinks", label: "Drinks" },
  { key: "food", label: "Food" },
  { key: "music", label: "Music & Entertainment" },
  { key: "wellness", label: "Wellness" },
  { key: "outdoors", label: "Outdoors & On-Water" },
  { key: "community", label: "Community" },
  { key: "celebration", label: "Celebrations" },
  { key: "special", label: "Special / Ticketed" },
]);

const DEFAULT_GRACE = Object.freeze({ before: 15, after: 30 });

export const EVENT_TEMPLATES = Object.freeze([
  // ----- Drinks ----------------------------------------------------------
  {
    key: "happy-hour",
    label: "Happy Hour",
    icon: faGlassMartiniAlt,
    category: "drinks",
    shortDescription: "A recurring discounted drinks window — perfect for weekday foot traffic.",
    helpfulNotes:
      "Tip: pair this with an event-linked redeemable like '$1 off the first drink' to give members an extra reason to come in.",
    defaults: {
      type: EVENT_TYPE_KEYS.PUBLIC,
      intention: "happy-hour",
      title: "Happy Hour",
      description:
        "Join us for happy hour with discounted drinks and bites. Stop in and unwind with the crew.",
      tags: ["drinks", "happy-hour"],
      recurrence: { freq: "WEEKLY", interval: 1, byDay: ["MO", "TU", "WE", "TH", "FR"], until: null, count: null, exDates: [] },
      timeWindow: { start: "16:00", end: "18:00" },
      durationMinutes: 120,
      capacity: null,
      doubloonReward: 1,
      ticketPriceCents: 0,
      graceWindowMinutes: { before: 15, after: 60 },
      suggestedRedeemable: {
        name: "$1 off first drink",
        description: "One drink discount per member, valid only during happy hour.",
        tokenCost: 1,
        maxRedemptionsPerUser: 1,
      },
    },
  },
  {
    key: "wine-wednesday",
    label: "Wine Wednesday",
    icon: faWineGlass,
    category: "drinks",
    shortDescription: "A weekly themed wine night — features, flights, or by-the-glass specials.",
    helpfulNotes: "Use the description to call out featured varietals or sommelier picks.",
    defaults: {
      type: EVENT_TYPE_KEYS.PUBLIC,
      intention: "themed-night",
      title: "Wine Wednesday",
      description:
        "Half-off select bottles and curated flights every Wednesday. Featured varietal each week.",
      tags: ["drinks", "wine"],
      recurrence: { freq: "WEEKLY", interval: 1, byDay: ["WE"], until: null, count: null, exDates: [] },
      timeWindow: { start: "17:00", end: "21:00" },
      durationMinutes: 240,
      capacity: null,
      doubloonReward: 1,
      ticketPriceCents: 0,
      graceWindowMinutes: DEFAULT_GRACE,
      suggestedRedeemable: null,
    },
  },
  {
    key: "ladies-night",
    label: "Ladies' Night",
    icon: faVenus,
    category: "drinks",
    shortDescription: "Weekly themed night with featured cocktails and specials.",
    helpfulNotes: "Highlight any signature cocktails or live entertainment in the description.",
    defaults: {
      type: EVENT_TYPE_KEYS.PUBLIC,
      intention: "themed-night",
      title: "Ladies' Night",
      description: "Featured cocktails and house specials. The crew toasts the captains of the night.",
      tags: ["drinks", "ladies-night"],
      recurrence: { freq: "WEEKLY", interval: 1, byDay: ["TH"], until: null, count: null, exDates: [] },
      timeWindow: { start: "19:00", end: "23:00" },
      durationMinutes: 240,
      capacity: null,
      doubloonReward: 1,
      ticketPriceCents: 0,
      graceWindowMinutes: DEFAULT_GRACE,
      suggestedRedeemable: null,
    },
  },

  // ----- Food ------------------------------------------------------------
  {
    key: "taco-tuesday",
    label: "Taco Tuesday",
    icon: faPepperHot,
    category: "food",
    shortDescription: "Weekly cuisine-themed special — drive foot traffic on the slowest weekday.",
    helpfulNotes: "Mention any taco/margarita pricing in the description so members know what to expect.",
    defaults: {
      type: EVENT_TYPE_KEYS.PUBLIC,
      intention: "themed-night",
      title: "Taco Tuesday",
      description: "Tacos, margaritas, and a good time every Tuesday. Bring your crew.",
      tags: ["food", "tacos"],
      recurrence: { freq: "WEEKLY", interval: 1, byDay: ["TU"], until: null, count: null, exDates: [] },
      timeWindow: { start: "17:00", end: "22:00" },
      durationMinutes: 300,
      capacity: null,
      doubloonReward: 1,
      ticketPriceCents: 0,
      graceWindowMinutes: DEFAULT_GRACE,
      suggestedRedeemable: null,
    },
  },
  {
    key: "sunday-brunch",
    label: "Sunday Brunch",
    icon: faMugSaucer,
    category: "food",
    shortDescription: "A weekly weekend brunch — leverage the Sunday-funday crowd.",
    helpfulNotes: "Consider adding an event-only redeemable for a bottomless mimosa upgrade.",
    defaults: {
      type: EVENT_TYPE_KEYS.PUBLIC,
      intention: "brunch",
      title: "Sunday Brunch",
      description: "Brunch favorites, bottomless options, and island vibes every Sunday.",
      tags: ["food", "brunch"],
      recurrence: { freq: "WEEKLY", interval: 1, byDay: ["SU"], until: null, count: null, exDates: [] },
      timeWindow: { start: "10:00", end: "14:00" },
      durationMinutes: 240,
      capacity: null,
      doubloonReward: 2,
      ticketPriceCents: 0,
      graceWindowMinutes: DEFAULT_GRACE,
      suggestedRedeemable: null,
    },
  },
  {
    key: "chefs-tasting",
    label: "Chef's Tasting Menu",
    icon: faKitchenSet,
    category: "food",
    shortDescription: "A one-night or recurring multi-course tasting from the chef.",
    helpfulNotes:
      "Set a capacity to manage kitchen flow. Mark as ticketed if you want guests to prepay.",
    defaults: {
      type: EVENT_TYPE_KEYS.TICKETED,
      intention: "tasting",
      title: "Chef's Tasting Menu",
      description: "A multi-course tasting menu prepared by our chef. Seating limited.",
      tags: ["food", "tasting"],
      recurrence: null,
      timeWindow: { start: "18:30", end: "21:30" },
      durationMinutes: 180,
      capacity: 24,
      doubloonReward: 5,
      ticketPriceCents: 7500,
      graceWindowMinutes: DEFAULT_GRACE,
      suggestedRedeemable: null,
    },
  },
  {
    key: "cooking-class",
    label: "Cooking Class / Demo",
    icon: faKitchenSet,
    category: "food",
    shortDescription: "Hands-on or demo-style class with the chef.",
    helpfulNotes: "Use capacity to set class size. Add any required materials in the description.",
    defaults: {
      type: EVENT_TYPE_KEYS.TICKETED,
      intention: "class",
      title: "Cooking Class",
      description: "Learn a signature dish hands-on with the chef. All materials and tastings included.",
      tags: ["food", "class"],
      recurrence: null,
      timeWindow: { start: "11:00", end: "13:00" },
      durationMinutes: 120,
      capacity: 12,
      doubloonReward: 3,
      ticketPriceCents: 4500,
      graceWindowMinutes: DEFAULT_GRACE,
      suggestedRedeemable: null,
    },
  },

  // ----- Music & Entertainment ------------------------------------------
  {
    key: "live-music",
    label: "Live Music Night",
    icon: faMusic,
    category: "music",
    shortDescription: "Recurring live music — set day of week and time window.",
    helpfulNotes: "Drop the artist name into the title each week, or keep it generic for the series.",
    defaults: {
      type: EVENT_TYPE_KEYS.PUBLIC,
      intention: "live-music",
      title: "Live Music Night",
      description: "Live music from local artists. Free to attend — drinks and bites available.",
      tags: ["music", "live"],
      recurrence: { freq: "WEEKLY", interval: 1, byDay: ["FR"], until: null, count: null, exDates: [] },
      timeWindow: { start: "20:00", end: "23:00" },
      durationMinutes: 180,
      capacity: null,
      doubloonReward: 2,
      ticketPriceCents: 0,
      graceWindowMinutes: DEFAULT_GRACE,
      suggestedRedeemable: null,
    },
  },
  {
    key: "trivia-night",
    label: "Trivia Night",
    icon: faPuzzlePiece,
    category: "music",
    shortDescription: "Recurring pub trivia — great for repeat foot traffic.",
    helpfulNotes: "List prizes (gift cards, swag, doubloons) in the description.",
    defaults: {
      type: EVENT_TYPE_KEYS.PUBLIC,
      intention: "trivia",
      title: "Trivia Night",
      description: "Test your knowledge — prizes for the top teams. Free to play.",
      tags: ["games", "trivia"],
      recurrence: { freq: "WEEKLY", interval: 1, byDay: ["WE"], until: null, count: null, exDates: [] },
      timeWindow: { start: "19:00", end: "21:00" },
      durationMinutes: 120,
      capacity: null,
      doubloonReward: 2,
      ticketPriceCents: 0,
      graceWindowMinutes: DEFAULT_GRACE,
      suggestedRedeemable: null,
    },
  },
  {
    key: "karaoke-night",
    label: "Karaoke Night",
    icon: faMicrophone,
    category: "music",
    shortDescription: "Weekly karaoke — bring the crowd.",
    helpfulNotes: "Mention any drink/song specials in the description.",
    defaults: {
      type: EVENT_TYPE_KEYS.PUBLIC,
      intention: "karaoke",
      title: "Karaoke Night",
      description: "Grab the mic and sing your heart out. Open list, all welcome.",
      tags: ["music", "karaoke"],
      recurrence: { freq: "WEEKLY", interval: 1, byDay: ["SA"], until: null, count: null, exDates: [] },
      timeWindow: { start: "20:00", end: "24:00" },
      durationMinutes: 240,
      capacity: null,
      doubloonReward: 1,
      ticketPriceCents: 0,
      graceWindowMinutes: DEFAULT_GRACE,
      suggestedRedeemable: null,
    },
  },
  {
    key: "open-mic",
    label: "Open Mic",
    icon: faMicrophoneAlt,
    category: "music",
    shortDescription: "Weekly open mic for musicians, poets, and comedians.",
    helpfulNotes: "Note signup procedures (in-person, online) in the description.",
    defaults: {
      type: EVENT_TYPE_KEYS.PUBLIC,
      intention: "open-mic",
      title: "Open Mic Night",
      description: "An open stage for musicians, poets, and comedians. Sign up at the door.",
      tags: ["music", "open-mic"],
      recurrence: { freq: "WEEKLY", interval: 1, byDay: ["TH"], until: null, count: null, exDates: [] },
      timeWindow: { start: "19:30", end: "22:30" },
      durationMinutes: 180,
      capacity: null,
      doubloonReward: 1,
      ticketPriceCents: 0,
      graceWindowMinutes: DEFAULT_GRACE,
      suggestedRedeemable: null,
    },
  },
  {
    key: "dj-night",
    label: "DJ Set / Dance Party",
    icon: faCompactDisc,
    category: "music",
    shortDescription: "Recurring DJ-driven dance night.",
    helpfulNotes: "Call out the genre or featured DJ in the title for each occurrence.",
    defaults: {
      type: EVENT_TYPE_KEYS.PUBLIC,
      intention: "dj-night",
      title: "DJ Night",
      description: "Resident DJ on the decks. Dance into the night.",
      tags: ["music", "dance"],
      recurrence: { freq: "WEEKLY", interval: 1, byDay: ["FR", "SA"], until: null, count: null, exDates: [] },
      timeWindow: { start: "22:00", end: "26:00" },
      durationMinutes: 240,
      capacity: null,
      doubloonReward: 1,
      ticketPriceCents: 0,
      graceWindowMinutes: DEFAULT_GRACE,
      suggestedRedeemable: null,
    },
  },
  {
    key: "watch-party",
    label: "Game Day Watch Party",
    icon: faFootballBall,
    category: "music",
    shortDescription: "Sports watch party — set day of week per season.",
    helpfulNotes: "Mention game/teams featured and any food/drink specials.",
    defaults: {
      type: EVENT_TYPE_KEYS.PUBLIC,
      intention: "watch-party",
      title: "Game Day Watch Party",
      description: "Big screens, cold beers, and game-day specials. Catch every play with the crew.",
      tags: ["sports", "watch-party"],
      recurrence: { freq: "WEEKLY", interval: 1, byDay: ["SU"], until: null, count: null, exDates: [] },
      timeWindow: { start: "13:00", end: "20:00" },
      durationMinutes: 420,
      capacity: null,
      doubloonReward: 1,
      ticketPriceCents: 0,
      graceWindowMinutes: DEFAULT_GRACE,
      suggestedRedeemable: null,
    },
  },

  // ----- Wellness --------------------------------------------------------
  {
    key: "yoga-class",
    label: "Yoga / Wellness Class",
    icon: faSpa,
    category: "wellness",
    shortDescription: "Weekly yoga or wellness session — beach, dock, or studio.",
    helpfulNotes: "Mention what attendees should bring (mat, water, towel).",
    defaults: {
      type: EVENT_TYPE_KEYS.PUBLIC,
      intention: "yoga",
      title: "Beach Yoga",
      description: "All-levels yoga class on the beach. Bring a mat and water.",
      tags: ["wellness", "yoga"],
      recurrence: { freq: "WEEKLY", interval: 1, byDay: ["SA", "SU"], until: null, count: null, exDates: [] },
      timeWindow: { start: "08:00", end: "09:00" },
      durationMinutes: 60,
      capacity: 20,
      doubloonReward: 2,
      ticketPriceCents: 0,
      graceWindowMinutes: DEFAULT_GRACE,
      suggestedRedeemable: null,
    },
  },

  // ----- Outdoors & On-Water --------------------------------------------
  {
    key: "sunset-sail",
    label: "Sunset Sail",
    icon: faShip,
    category: "outdoors",
    shortDescription: "Recurring sunset cruise — typically ticketed with limited capacity.",
    helpfulNotes:
      "Set a realistic capacity for the vessel and check tides — adjust the time window seasonally.",
    defaults: {
      type: EVENT_TYPE_KEYS.TICKETED,
      intention: "sail",
      title: "Sunset Sail",
      description: "A relaxing sunset cruise around the harbor. Drinks and light bites included.",
      tags: ["outdoors", "sail", "sunset"],
      recurrence: { freq: "WEEKLY", interval: 1, byDay: ["WE", "FR", "SA"], until: null, count: null, exDates: [] },
      timeWindow: { start: "17:00", end: "19:30" },
      durationMinutes: 150,
      capacity: 24,
      doubloonReward: 5,
      ticketPriceCents: 8500,
      graceWindowMinutes: { before: 30, after: 0 },
      suggestedRedeemable: null,
    },
  },
  {
    key: "private-charter",
    label: "Private Charter Day",
    icon: faAnchor,
    category: "outdoors",
    shortDescription: "Full-day charter for a single group.",
    helpfulNotes: "Use this for blocking out the boat. Typically one-time, ticketed.",
    defaults: {
      type: EVENT_TYPE_KEYS.TICKETED,
      intention: "charter",
      title: "Private Charter",
      description: "A full-day private charter — itinerary customizable.",
      tags: ["outdoors", "charter"],
      recurrence: null,
      timeWindow: { start: "09:00", end: "16:00" },
      durationMinutes: 420,
      capacity: 8,
      doubloonReward: 10,
      ticketPriceCents: 95000,
      graceWindowMinutes: { before: 30, after: 0 },
      suggestedRedeemable: null,
    },
  },
  {
    key: "beach-bonfire",
    label: "Beach Bonfire",
    icon: faFire,
    category: "outdoors",
    shortDescription: "Recurring beach bonfire — drinks, s'mores, music.",
    helpfulNotes: "Mention any permits or weather-cancellation policy in the description.",
    defaults: {
      type: EVENT_TYPE_KEYS.PUBLIC,
      intention: "bonfire",
      title: "Beach Bonfire",
      description: "A community bonfire on the beach. BYO chair and good vibes.",
      tags: ["outdoors", "beach", "bonfire"],
      recurrence: { freq: "WEEKLY", interval: 1, byDay: ["FR"], until: null, count: null, exDates: [] },
      timeWindow: { start: "19:00", end: "22:00" },
      durationMinutes: 180,
      capacity: null,
      doubloonReward: 2,
      ticketPriceCents: 0,
      graceWindowMinutes: DEFAULT_GRACE,
      suggestedRedeemable: null,
    },
  },
  {
    key: "treasure-hunt",
    label: "Treasure Hunt",
    icon: faMapMarkedAlt,
    category: "outdoors",
    shortDescription: "A guided treasure hunt — leans into the Pirate's Guild theme.",
    helpfulNotes: "Define start point, expected duration, and any prizes in the description.",
    defaults: {
      type: EVENT_TYPE_KEYS.TREASURE_HUNT,
      intention: "treasure-hunt",
      title: "Treasure Hunt",
      description: "Follow the clues across the island — prizes for the top crews.",
      tags: ["outdoors", "treasure-hunt", "game"],
      recurrence: null,
      timeWindow: { start: "10:00", end: "13:00" },
      durationMinutes: 180,
      capacity: 60,
      doubloonReward: 10,
      ticketPriceCents: 0,
      graceWindowMinutes: DEFAULT_GRACE,
      suggestedRedeemable: null,
    },
  },

  // ----- Community -------------------------------------------------------
  {
    key: "networking-mixer",
    label: "Networking Mixer",
    icon: faHandshake,
    category: "community",
    shortDescription: "Professional or community networking event.",
    helpfulNotes: "RSVP-only is often the right pricing mode here.",
    defaults: {
      type: EVENT_TYPE_KEYS.NETWORKING,
      intention: "networking",
      title: "Networking Mixer",
      description: "An evening for local professionals to connect. Light bites + cash bar.",
      tags: ["networking", "community"],
      recurrence: null,
      timeWindow: { start: "18:00", end: "20:00" },
      durationMinutes: 120,
      capacity: 75,
      doubloonReward: 3,
      ticketPriceCents: 0,
      graceWindowMinutes: DEFAULT_GRACE,
      suggestedRedeemable: null,
    },
  },
  {
    key: "vip-night",
    label: "VIP Member Night",
    icon: faStar,
    category: "community",
    shortDescription: "Exclusive event for top-tier members.",
    helpfulNotes: "Mark as VIP type to flag the audience. Capacity-limited is recommended.",
    defaults: {
      type: EVENT_TYPE_KEYS.VIP,
      intention: "vip",
      title: "VIP Member Night",
      description: "A members-only evening of exclusive tastings and surprises.",
      tags: ["vip", "members"],
      recurrence: null,
      timeWindow: { start: "19:00", end: "22:00" },
      durationMinutes: 180,
      capacity: 40,
      doubloonReward: 10,
      ticketPriceCents: 0,
      graceWindowMinutes: DEFAULT_GRACE,
      suggestedRedeemable: null,
    },
  },

  // ----- Celebrations ----------------------------------------------------
  {
    key: "grand-opening",
    label: "Grand Opening / Anniversary",
    icon: faBirthdayCake,
    category: "celebration",
    shortDescription: "One-off celebration — opening day, anniversary, milestone.",
    helpfulNotes: "Consider a generous doubloon reward and a dedicated redeemable for the day.",
    defaults: {
      type: EVENT_TYPE_KEYS.FEATURED,
      intention: "celebration",
      title: "Grand Opening Celebration",
      description: "Join us for live music, complimentary bites, and special offers all day.",
      tags: ["celebration", "opening"],
      recurrence: null,
      timeWindow: { start: "12:00", end: "22:00" },
      durationMinutes: 600,
      capacity: null,
      doubloonReward: 5,
      ticketPriceCents: 0,
      graceWindowMinutes: DEFAULT_GRACE,
      suggestedRedeemable: null,
    },
  },
  {
    key: "holiday-celebration",
    label: "Holiday Celebration",
    icon: faSnowflake,
    category: "celebration",
    shortDescription: "Holiday-themed celebration: NYE, July 4th, Carnival, Christmas, etc.",
    helpfulNotes: "Update the title each holiday — defaults are generic.",
    defaults: {
      type: EVENT_TYPE_KEYS.FEATURED,
      intention: "holiday",
      title: "Holiday Celebration",
      description: "Celebrate the holiday with us — themed specials, music, and good company.",
      tags: ["celebration", "holiday"],
      recurrence: null,
      timeWindow: { start: "18:00", end: "23:00" },
      durationMinutes: 300,
      capacity: null,
      doubloonReward: 3,
      ticketPriceCents: 0,
      graceWindowMinutes: DEFAULT_GRACE,
      suggestedRedeemable: null,
    },
  },

  // ----- Special / Ticketed ---------------------------------------------
  {
    key: "ticketed-special",
    label: "Ticketed Special Event",
    icon: faTicket,
    category: "special",
    shortDescription: "One-off ticketed concert, show, or experience.",
    helpfulNotes: "Set price, capacity, and a clear cancellation policy in the description.",
    defaults: {
      type: EVENT_TYPE_KEYS.TICKETED,
      intention: "ticketed-special",
      title: "Special Event",
      description: "A one-night-only special event. Limited tickets available.",
      tags: ["ticketed", "special"],
      recurrence: null,
      timeWindow: { start: "19:00", end: "22:00" },
      durationMinutes: 180,
      capacity: 100,
      doubloonReward: 5,
      ticketPriceCents: 5000,
      graceWindowMinutes: DEFAULT_GRACE,
      suggestedRedeemable: null,
    },
  },
  // ----- Blank fallback (used by "Start from scratch" tile) -------------
  {
    key: "blank",
    label: "Start from scratch",
    icon: faCalendarPlus,
    category: "special",
    shortDescription: "Build any event from a blank slate. Use the helpful tips inside.",
    helpfulNotes: "Every field has a tooltip explaining what it does and when to use it.",
    defaults: {
      type: EVENT_TYPE_KEYS.PUBLIC,
      intention: "custom",
      title: "",
      description: "",
      tags: [],
      recurrence: null,
      timeWindow: null,
      durationMinutes: null,
      capacity: null,
      doubloonReward: 0,
      ticketPriceCents: 0,
      graceWindowMinutes: { before: 15, after: 30 },
      suggestedRedeemable: null,
    },
  },
]);

export function getEventTemplate(key) {
  return EVENT_TEMPLATES.find((t) => t.key === key) || null;
}

export function getTemplatesByCategory(categoryKey) {
  return EVENT_TEMPLATES.filter((t) => t.category === categoryKey);
}
